import type { Theme } from "@mui/material/styles";

export const BORDER_WIDTH_OPTIONS: Array<{ label: string; value: number }> = [
    { label: "None", value: 0 },
    { label: "1px", value: 1 },
    { label: "2px", value: 2 },
    { label: "3px", value: 3 },
    { label: "4px", value: 4 },
];

export const RADIUS_OPTIONS: Array<{ label: string; value: number }> = [
    { label: "None", value: 0 },
    { label: "XS", value: 0.5 },
    { label: "S", value: 0.75 },
    { label: "M", value: 1 },
    { label: "L", value: 1.5 },
    { label: "XL", value: 2 },
];

export const SPACING_OPTIONS: Array<{ label: string; value: number }> = [
    { label: "None", value: 0 },
    { label: "XS", value: 1 },
    { label: "S", value: 2 },
    { label: "M", value: 3 },
    { label: "L", value: 4 },
    { label: "XL", value: 6 },
    { label: "2XL", value: 8 },
    { label: "3XL", value: 12 },
];

export type TextSpacing = number | "default";

export const TEXT_SPACING_OPTIONS: Array<{ label: string; value: TextSpacing }> = [
    { label: "Default", value: "default" },
    ...SPACING_OPTIONS,
];

export const TEXT_SPACING_ATTRIBUTE = "data-text-spacing";

const blockWithSpacing = `[${TEXT_SPACING_ATTRIBUTE}]`;
const editorCanvasBlock = "[data-puck-component]";
const emptyLine = ":is(p, h1, h2, h3, h4):empty";
const firstFilledChild = `& > :not(${emptyLine}):not(:not(${emptyLine}) ~ *)`;
const lastFilledChild = `& > :not(${emptyLine}):not(:has(~ :not(${emptyLine})))`;
const leadingEmptyLines = `& > ${emptyLine}:not(:not(${emptyLine}) ~ *)`;
const trailingEmptyLines = `& > ${emptyLine}:not(:has(~ :not(${emptyLine})))`;
const topOfContent = [
    firstFilledChild,
    `${firstFilledChild} > p:not(* + *)`,
    `${firstFilledChild} > li:not(* + *) > p:not(* + *)`,
].join(", ");
const bottomOfContent = [
    lastFilledChild,
    `${lastFilledChild} > p:last-child`,
    `${lastFilledChild} li:last-child > p:last-child:not(li:not(:last-child) *)`,
].join(", ");
const trimmedTop = { [leadingEmptyLines]: { display: "none" }, [topOfContent]: { mt: 0 } };
const trimmedBottom = { [trailingEmptyLines]: { display: "none" }, [bottomOfContent]: { mb: 0 } };
const afterBlockWithSpacing = [
    `${blockWithSpacing} + &`,
    `${blockWithSpacing} + a > &`,
    `${editorCanvasBlock}:has(> ${blockWithSpacing}) + ${editorCanvasBlock} > &`,
    `${editorCanvasBlock}:has(> ${blockWithSpacing}) + ${editorCanvasBlock} > a > &`,
].join(", ");
const beforeBlockWithSpacing = [
    `&:has(+ ${blockWithSpacing})`,
    `a:has(+ ${blockWithSpacing}) > &`,
    `${editorCanvasBlock}:has(+ ${editorCanvasBlock} > ${blockWithSpacing}) > &`,
    `${editorCanvasBlock}:has(+ ${editorCanvasBlock} > ${blockWithSpacing}) > a > &`,
].join(", ");

export const textSpacingAttributes = (spacing: TextSpacing | undefined) =>
    typeof spacing === "number" ? { [TEXT_SPACING_ATTRIBUTE]: spacing } : {};

export const textSpacingSx = (spacing: TextSpacing | undefined) =>
    typeof spacing === "number" ?
        { display: "block", my: spacing, ...trimmedTop, ...trimmedBottom }
    :   { [afterBlockWithSpacing]: trimmedTop, [beforeBlockWithSpacing]: trimmedBottom };

export const resolveRadius = (theme: Theme, radiusScale: number | undefined): string | number | undefined => {
    if (radiusScale === undefined) return undefined;
    if (radiusScale <= 0) return 0;
    return theme.spacing(radiusScale);
};

export const resolveSpacing = (theme: Theme, spacingScale: number | undefined): string | undefined => {
    if (spacingScale === undefined) return undefined;
    if (spacingScale <= 0) return theme.spacing(0);
    return theme.spacing(spacingScale);
};
